import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryDetails } from '../../../../hooks/QueryBuilder';
import { QueryField, QueryGroup, QueryRule, QuerySource, OperatorValue } from '../../../../types/QueryBuilder';
import { convertSavedQueryToGroups, getSimpleQueryFields, toQuerySource } from '../../../../utils/queryBuilderUtils';
import SelectionSummary from '../../../Clients/QueryBuilder/components/SimpleQuery/SelectionSummary';

const OPERATOR_LABELS: Partial<Record<OperatorValue, string>> = {
  '=': 'est égal à',
  '!=': 'est différent de',
  '>': 'supérieur à',
  '<': 'inférieur à',
  '>=': 'supérieur ou égal à',
  '<=': 'inférieur ou égal à',
  between: 'entre',
  in: 'parmi',
  'not in': 'ne fait pas partie de',
  contains: 'contient',
  'not contains': 'ne contient pas',
  beginsWith: 'commence par',
};

function mapRulesToGroups(convertedGroups: QueryGroup[], fields: QueryField[], categories: string[]): QueryGroup[] {
  return convertedGroups.map(group => ({
    ...group,
    rules: group.rules.map(rule => {
      const field = fields.find(f => f.name === rule.field);
      return {
        ...rule,
        category: field?.Categorie || categories[0],
        operator: rule.operator || '=',
        value: rule.value ?? '',
      };
    }),
  }));
}

function formatRuleValue(rule: QueryRule): string {
  if (rule.operator === 'between') {
    const raw = String(rule.value ?? '');
    const [min, max] = raw.includes('|') ? raw.split('|') : [raw, String(rule.valueTo ?? '')];
    return `${min || '?'} et ${max || '?'}`;
  }
  if (Array.isArray(rule.value)) return rule.value.join(', ');
  return String(rule.value ?? '');
}

function AdvancedGroupsPreview({ groups, fields }: { groups: QueryGroup[]; fields: QueryField[] }) {
  return (
    <Box>
      {groups.map((group, groupIndex) => (
        <Box key={group.id}>
          {groupIndex > 0 && (
            <Box sx={{ textAlign: 'center', my: 1.5 }}>
              <Chip label="OU" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 600 }} size="small" />
            </Box>
          )}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, p: 1.5 }}>
            {group.rules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucune règle</Typography>
            ) : (
              group.rules.map((rule, ruleIndex) => {
                const field = fields.find(f => f.name === rule.field);
                return (
                  <Box key={rule.id}>
                    {ruleIndex > 0 && (
                      <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600 }}>ET</Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{field?.label ?? rule.field}</strong>{' '}
                      {rule.operator ? OPERATOR_LABELS[rule.operator] ?? rule.operator : ''}{' '}
                      <strong>{formatRuleValue(rule)}</strong>
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export interface QueryPreviewTarget {
  queryId: string;
  description: string;
}

interface QueryPreviewDialogProps {
  open: boolean;
  query: QueryPreviewTarget | null;
  fields: QueryField[];
  categories: string[];
  confirming?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function QueryPreviewDialog({ open, query, fields, categories, confirming = false, onClose, onConfirm }: QueryPreviewDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { fetchQueryDetails } = useQueryDetails();

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<QueryGroup[]>([]);
  const [source, setSource] = useState<QuerySource>('SIMPLE');
  const fieldsRef = useRef(fields);
  const categoriesRef = useRef(categories);
  fieldsRef.current = fields;
  categoriesRef.current = categories;

  useEffect(() => {
    if (!open || !query) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setGroups([]);
      try {
        const details = await fetchQueryDetails(query.queryId);
        if (cancelled) return;
        if (!details) return;

        setSource(toQuerySource(details.SOURCE));

        let conditions = details.conditions ?? null;
        if (typeof conditions === 'string') {
          try { conditions = JSON.parse(conditions); } catch { conditions = null; }
        }
        if (conditions) {
          try {
            const converted = convertSavedQueryToGroups({ ...details, conditions } as Parameters<typeof convertSavedQueryToGroups>[0]);
            if (converted?.length) {
              setGroups(mapRulesToGroups(converted, fieldsRef.current, categoriesRef.current));
            }
          } catch { /* leave groups empty on parse failure */ }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, query, fetchQueryDetails]);

  const simpleFields = getSimpleQueryFields(fields);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Aperçu de la requête{query ? ` — ${query.description}` : ''}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
            <CircularProgress />
          </Box>
        ) : groups.length === 0 || groups.every(g => g.rules.length === 0) ? (
          <Typography variant="body2" color="text.secondary">
            Aucun détail de règle n&apos;a pu être récupéré pour cette requête.
          </Typography>
        ) : source === 'SIMPLE' ? (
          <SelectionSummary fields={simpleFields} rules={groups[0]?.rules ?? []} />
        ) : (
          <AdvancedGroupsPreview groups={groups} fields={fields} />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
          Annuler
        </Button>
        <Button
          variant="contained"
          startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          onClick={onConfirm}
          disabled={confirming}
          sx={{ textTransform: 'none', bgcolor: '#556ee6', '&:hover': { bgcolor: '#4558c8' } }}
        >
          Sélectionner cette requête
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QueryPreviewDialog;
